import { effectivePrice, type ShopProduct } from '@hunk/shared/api/shop';
import { formatMoney } from '@hunk/shared/common';
import { productPath } from '@hunk/shared/productUrl';
import { Check, ImageOff, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router';

import { useCart } from '@/hooks/useCart';
import { cn } from '@/lib/utils';

/** "₪140" bold + "₪160" struck through when on sale */
export function Price({ product, className, big }: { product: Pick<ShopProduct, 'price' | 'salePrice'>; className?: string; big?: boolean }) {
  const now = effectivePrice(product);
  const onSale = now < product.price;
  return (
    <span className={cn('inline-flex items-baseline gap-2', className)}>
      <span className={cn('font-extrabold', big ? 'text-3xl' : 'text-lg')}>{formatMoney(now)}</span>
      {onSale && <span className={cn('opacity-60 line-through', big ? 'text-lg' : 'text-sm')}>{formatMoney(product.price)}</span>}
    </span>
  );
}

export function ProductImage({ src, alt, className }: { src?: string; alt: string; className?: string }) {
  return src ? (
    <img src={src} alt={alt} loading="lazy" className={cn('h-full w-full object-contain', className)} draggable={false} />
  ) : (
    <div className={cn('flex h-full w-full items-center justify-center bg-neutral-100 text-neutral-400', className)}>
      <ImageOff className="size-10" />
    </div>
  );
}

/** grid tile like hunk.co.il: white rounded card, photo, name, price, red pill "הוספה לסל" */
export function ProductCard({ product }: { product: ShopProduct }) {
  const { add, qtyOf } = useCart();
  const inCart = qtyOf(product.id) > 0;
  const discount = product.salePrice != null ? Math.round((1 - product.salePrice / product.price) * 100) : 0;
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-[22px] bg-white p-3 text-black shadow-sm transition-transform hover:-translate-y-0.5 md:p-4">
      {discount >= 5 && (
        <span dir="ltr" className="bg-primary absolute top-3 left-3 z-10 rounded-full px-2 py-0.5 text-xs font-bold text-white">
          -{discount}%
        </span>
      )}
      <Link to={productPath(product)} className="flex flex-1 flex-col">
        <div className="aspect-square overflow-hidden border-b border-neutral-300 pb-2">
          <ProductImage src={product.images[0]} alt={product.name} className="transition-transform duration-300 group-hover:scale-105" />
        </div>
        <h3 className="mt-3 line-clamp-2 min-h-[2.6em] text-center text-sm leading-snug md:text-base">{product.name}</h3>
        <div className="mt-2 text-center">
          <Price product={product} />
        </div>
      </Link>
      <div className="mt-3 flex justify-center">
        {product.inStock ? (
          <button
            type="button"
            onClick={() => add(product.id)}
            className="brand-grad brand-grad-hover flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold text-white shadow active:scale-95"
          >
            {inCart ? <Check className="size-4" /> : <ShoppingBag className="size-4" />}
            {inCart ? 'בסל · עוד אחד' : 'הוספה לסל'}
          </button>
        ) : (
          <span className="rounded-full bg-neutral-200 px-5 py-2 text-sm font-bold text-neutral-500">אזל מהמלאי</span>
        )}
      </div>
    </div>
  );
}

export function ProductGrid({ products }: { products: ShopProduct[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
      {products.map((p) => (
        <ProductCard key={p.id} product={p} />
      ))}
    </div>
  );
}
